import { Drawer } from '~/components/drawer';
import { FC, Suspense, useCallback } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { $NavDrawerOpen } from '~/models/nav-drawer';
import { useTranslation } from 'react-i18next';
import { Link } from '@remix-run/react';
import { DrawerCategoryList, DrawerCategoryListPlaceholder } from './category-list';
import { RegionSwitcher } from '~/components/region-switcher';
import DownloadIcon from 'icon/arrow_down_box.svg';
import EmailIcon from 'icon/envelope.svg';
import CategoryIcon from 'icon/square_stack_3d_up.svg';
import { ClickPosition } from '~/utils/click-position';
import { $Locale, $RootTraffic, $UserAgent } from '@lemon8/web-app-shared/atom';
import { usePageActive } from '~/utils/active/use-page-active';
import useReflowClick from '~/utils/use-reflow-click';
import { Debug } from '../debug';

import './style.scss';

export interface NavDrawerRef {
  changeTo: (open: boolean) => void;
}

export const NavDrawer: FC = () => {
  const [open, setOpen] = useAtom($NavDrawerOpen);
  const { t } = useTranslation();
  const { region } = useAtomValue($Locale);
  const active = usePageActive();

  const { isBot } = useAtomValue($UserAgent);
  const { enterPageName: pageName } = useAtomValue($RootTraffic);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleClickDownload = useReflowClick(
    {
      clickEvent: (_, bundle) => ({
        ...bundle,
        current_position: ClickPosition.side_bar_download,
      }),
      defaultBehavior: () => {
        active({
          schemaOption: {
            pageType: 'feed',
            categoryId: 0,
          },
          ampExtra: {
            enter_position: ClickPosition.side_bar_download,
          },
        });

        setOpen(false);
      },
    },
    [active],
  );

  const handleClick = useCallback(
    (position: ClickPosition) => () =>
      useReflowClick(
        {
          clickEvent: (_, bundle) => ({
            ...bundle,
            current_position: position,
          }),
          defaultBehavior: () => {
            setOpen(false);
          },
        },
        [setOpen],
      ),
    [],
  );

  return (
    <Drawer className="lemon8-nav-drawer" isOpen={open} onClose={handleClose} direction={'right-left'} showMask={true}>
      <div className="lemon8-nav-drawer-content">
        <div className="lemon8-nav-drawer-content-main">
          <ul className="nav-main-list">
            <li className="nav-main-list-item">
              <div className="title">
                <CategoryIcon />
                <span>{t('sidebar_channel')}</span>
              </div>
              <Suspense fallback={<DrawerCategoryListPlaceholder />}>
                <DrawerCategoryList />
              </Suspense>
            </li>
            <li className="nav-main-list-item">
              <div className="title" onClick={handleClickDownload}>
                <DownloadIcon />
                <span>{t('sidebar_download')}</span>
              </div>
            </li>
            <div className="divider" />
            <li className="nav-main-list-item">
              <a
                className="title"
                onClick={handleClick(ClickPosition.side_bar_email)}
                href={'mailto:contact@lemon8-app.com'}
              >
                <EmailIcon />
                <span>{t('sidebar_feedback')}</span>
              </a>
            </li>
            <div className="divider" />
            <li className="nav-main-list-item region-switcher">
              <RegionSwitcher className="nav-region-lang-switcher" />
            </li>
          </ul>
        </div>

        <div className="lemon8-nav-drawer-content-footer">
          {isBot && pageName === 'video' && (
            <a href="https://www.soundon.global/?ad_platform_id=seo_external_link&channel=lemon8">Music Distribution</a>
          )}
          <Link onClick={handleClick(ClickPosition.side_bar_homepage)} to={'/'} target="_blank">
            {t('official_website_entry')}
          </Link>
          <Link onClick={handleClick(ClickPosition.side_bar_pp)} to={`/legal/privacy?region=${region}`} target="_blank">
            {t('sidebar_privacy')}
          </Link>
          <Link
            onClick={handleClick(ClickPosition.side_bar_tos)}
            to={`/legal/terms-of-service?region=${region}`}
            target="_blank"
          >
            {t('sidebar_terms')}
          </Link>
          <Link
            onClick={handleClick(ClickPosition.side_bar_cookie)}
            to={`/legal/cookies-policy?region=${region}`}
            target="_blank"
          >
            Cookies Policy
          </Link>
          {__BUILD_TYPE__ !== 'online' && <Debug />}
          {region === 'gb' && (
            <Link
              onClick={handleClick(ClickPosition.side_bar_avmsd)}
              to={`/legal/avmsd?region=${region}`}
              target="_blank"
            >
              UK AVMS
            </Link>
          )}
        </div>
      </div>
    </Drawer>
  );
};
